/**
 * THE entitlement vocabulary - the exact strings a grant carries.
 *
 * A licence grant is `{"sections":[…],"ai_features":[…],"limits":{…}}` on the
 * catalogue row, and the only thing that makes a gate work is asking for a key
 * that is actually IN that array. There is no fuzzy matching, no aliasing, and
 * no error when you ask for one that does not exist - `includes()` simply says
 * no, and the page locks.
 *
 * WHY THIS FILE EXISTS. Backend migration `2026-05-07_licensing_v2_schema_and_data`
 * (Story 1205) reconciled the section names:
 *
 *     clients  →  contacts
 *     review   →  reviews
 *     + projects
 *     − client_pipeline, finder, lawyers
 *
 * Every grant in the catalogue was rewritten. The client's route map was not,
 * so it went on asking `hasSectionAccess('clients')` of a grant that says
 * `contacts`, and `('review')` of one that says `reviews` - and an org on PONS
 * Full, which is granted literally everything, had Contacts and Reviews locked
 * behind an upgrade wall for a plan it already had. A key typed as a bare
 * string in fourteen files cannot be kept in step by hand; it has to be named
 * once, which is what this is.
 *
 * Keep in step with the backend's `static_licenses.feature_gates`. If a key is
 * added there, add it here - `entitlementKeys.test.ts` asserts nothing in the
 * app asks for a key this file does not list.
 */

/* ------------------------------------------------------------------ */
/*  Sections                                                           */
/* ------------------------------------------------------------------ */

export const SECTIONS = {
  dashboard: 'dashboard',
  tasks: 'tasks',
  calendar: 'calendar',
  notifications: 'notifications',
  messages: 'messages',
  /** Renamed from `clients` (Story 1205). The entity is a Contact. */
  contacts: 'contacts',
  matters: 'matters',
  files: 'files',
  people: 'people',
  departments: 'departments',
  teams: 'teams',
  finance: 'finance',
  /** Added by Story 1205 - no client gate asked for it before. */
  projects: 'projects',
  research: 'research',
  tables: 'tables',
  analytics: 'analytics',
  data_sources: 'data_sources',
  templates: 'templates',
  /** Renamed from `review` (Story 1205). */
  reviews: 'reviews',
  /*
    Added by the v4.2 catalogue (backend efb8f20). Both are their own door.

    `matter_economics` is NOT `finance`: Starter carries `finance` (invoices,
    payments), while budgets, time entries and matter pricing are the €19
    Matter economics module. Gating the tab on `finance` would have opened it
    for every Starter firm.

    `drafting` is a top-level page (AB#1573), not a tab inside Matters, so it
    needs a door rather than borrowing `templates`.
  */
  matter_economics: 'matter_economics',
  drafting: 'drafting',
} as const;

export type SectionKey = (typeof SECTIONS)[keyof typeof SECTIONS];

export const ALL_SECTIONS: SectionKey[] = Object.values(SECTIONS);

/**
 * Keys that were REMOVED from every grant by Story 1205.
 *
 * Asking for one of these is not a typo the type system can catch - it is a
 * gate that can never open, because no catalogue row grants it any more. Named
 * here so the test can say which mistake was made rather than just "unknown
 * key".
 */
export const RETIRED_SECTIONS: Record<string, string> = {
  clients: 'contacts',
  review: 'reviews',
  /* The backend's canonical vocabulary (8b56a84) aliases the Feb–Apr seeds'
     `client_pipeline` onto what the May seed calls `projects`. */
  client_pipeline: 'projects',
  /* A typo in the Feb 2026 seed, aliased backend-side so a licence still
     carrying it opens Finance rather than silently granting nothing. */
  finances: 'finance',
  finder: '',
  lawyers: '',
};

/* ------------------------------------------------------------------ */
/*  AI features                                                        */
/* ------------------------------------------------------------------ */

/** Unchanged by Story 1205 - the AI vocabulary was already aligned. */
export const AI_FEATURES = {
  drafts: 'drafts',
  insights: 'insights',
  research: 'research',
  tables_ai: 'tables_ai',
  onboarding_ai: 'onboarding_ai',
  template_ai: 'template_ai',
  review_ai: 'review_ai',
  data_source_indexing: 'data_source_indexing',
} as const;

export type AiFeatureKey = (typeof AI_FEATURES)[keyof typeof AI_FEATURES];

export const ALL_AI_FEATURES: AiFeatureKey[] = Object.values(AI_FEATURES);

/* ------------------------------------------------------------------ */
/*  Route → section                                                    */
/* ------------------------------------------------------------------ */

/**
 * Which section a route belongs to.
 *
 * Read by the sidebar (to hide a nav item), by `LicenseOverlay` (to lock the
 * page and offer the upgrade), and by `checkRouteGate`. All three were wrong
 * for the same two routes, because all three read this one map.
 *
 * A route with NO entry is ungated - which is correct for `/settings`,
 * `/pricing`, `/packs` and the account pages. A firm at its limit is exactly
 * the firm that needs to reach the pages that sell it more.
 */
export const ROUTE_TO_SECTION: Record<string, SectionKey> = {
  '/dashboard': SECTIONS.dashboard,
  '/matters': SECTIONS.matters,
  '/contacts': SECTIONS.contacts,
  /* The pre-rename path still resolves; `/clients` redirects to `/contacts`,
     and a redirect that locks on the way through is a dead link. */
  '/clients': SECTIONS.contacts,
  '/projects': SECTIONS.projects,
  '/tasks': SECTIONS.tasks,
  '/files': SECTIONS.files,
  '/research': SECTIONS.research,
  '/tables': SECTIONS.tables,
  '/analytics': SECTIONS.analytics,
  '/data-sources': SECTIONS.data_sources,
  '/templates': SECTIONS.templates,
  '/finance': SECTIONS.finance,
  '/people': SECTIONS.people,
  '/departments': SECTIONS.departments,
  '/teams': SECTIONS.teams,
  '/calendar': SECTIONS.calendar,
  '/messages': SECTIONS.messages,
  '/reviews': SECTIONS.reviews,
  '/playbooks': SECTIONS.reviews,
  '/drafting': SECTIONS.drafting,
  /*
    TIME TRACKING IS MATTER ECONOMICS. "My time", time keeping and time
    tracking are the same €19 module as the matter's Economics tab - the tab
    was gated and these three pages were not, so the page rendered in full and
    only the API refused, which is a lock the user meets as a broken table
    rather than as an upgrade.
  */
  '/my-time': SECTIONS.matter_economics,
  '/time-tracking': SECTIONS.matter_economics,
  '/time-keeping': SECTIONS.matter_economics,
  '/notifications': SECTIONS.notifications,
  /*
    ROUTES THAT WERE GATED ONLY BY THEIR API.

    Each of these rendered, fetched, took a 403 and raised the error-handler
    modal - a full-viewport dialog that also covers the sidebar - instead of
    the page overlay. That is the "two different lock screens" report: same
    card, two shells, because the route was missing here.

    `/economics` is the FIRM economics dashboard, which reads
    `/organizations/me/economics` and is guarded on `analytics` (firm-wide
    reporting), not on the per-matter `matter_economics`.

    `/strategy` is Drafting under another name - a strategy is a kind of draft,
    and holding Drafting grants it.

    DELIBERATELY ABSENT, and each for its own reason:
      * `/pipeline` - a separate feature from Projects, and not sold yet. I had
        it on `projects` because the backend aliases the retired
        `client_pipeline` there; that alias is about a dead grant key, not
        about what the page is. Nothing gates it.
      * `/audit-log` - every package includes audit logs.
      * `/lawyers`, `/finder` - not ready.
      * `/operations` - firm operations, staying open.
    An unmapped route is an open one; that is the whole mechanism.
  */
  '/economics': SECTIONS.analytics,
  '/research2': SECTIONS.research,
  '/strategy': SECTIONS.drafting,
};

/** Human label for a section, for the lock screen and the upgrade modal. */
export const SECTION_LABELS: Record<SectionKey, string> = {
  dashboard: 'Dashboard',
  tasks: 'Tasks & Deadlines',
  calendar: 'Calendar',
  notifications: 'Notifications',
  messages: 'Message Center',
  contacts: 'Contacts',
  matters: 'Matters',
  files: 'Document Management',
  people: 'People Management',
  departments: 'Departments',
  teams: 'Teams',
  finance: 'Finance & Billing',
  projects: 'Projects',
  research: 'Research',
  tables: 'Tables',
  analytics: 'Analytics',
  data_sources: 'Knowledge',
  templates: 'Templates',
  reviews: 'Review',
  matter_economics: 'Matter economics',
  drafting: 'Drafting & strategies',
};

export const AI_FEATURE_LABELS: Record<AiFeatureKey, string> = {
  drafts: 'AI Drafting',
  insights: 'Matter Insights',
  research: 'AI Research',
  tables_ai: 'AI Tables',
  onboarding_ai: 'AI Client Onboarding',
  template_ai: 'AI Templates',
  review_ai: 'AI Contract Review',
  data_source_indexing: 'Knowledge Base Processing',
};

/**
 * A NAME, always - never a blank and never a raw key.
 *
 * This is the sentence "Your current plan does not include ___", so an empty
 * label is a sentence that says nothing and a raw key is one that says
 * `matter_economics`. Falls back through: the canonical label, then the key
 * humanised (`matter_economics` → "Matter economics"), then a generic noun,
 * so no gate can ever render the blank the lock card showed.
 */
const humanise = (key: string): string => {
  const words = String(key || '').replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
};

export const sectionLabel = (section: string): string =>
  SECTION_LABELS[section as SectionKey] || humanise(section) || 'this feature';

export const aiFeatureLabel = (feature: string): string =>
  AI_FEATURE_LABELS[feature as AiFeatureKey] || humanise(feature) || 'this feature';

/* ------------------------------------------------------------------ */
/*  Where "Upgrade" goes                                               */
/* ------------------------------------------------------------------ */

/**
 * The one destination for every upgrade call to action.
 *
 * Every one of them used to push `/account?tab=plans`. There is no `/account`
 * route - `PrivateRoutes` ends in `path="*"` → `NotFoundPage` - so a user who
 * hit a licence wall, read "Upgrade to PONS Full to unlock this feature" and
 * pressed the button was taken to a 404. The lock worked; the way out of it
 * did not, in the lockout overlay, the upgrade modal, the licence banner, the
 * no-seat gate and the error handler alike.
 *
 * `/plans` is the page that can actually change a plan (checkout, add-ons,
 * seats). `/pricing` is the read-only sheet, which is the wrong place to send
 * somebody who has already decided to buy.
 */
export const UPGRADE_PATH = '/plans';

/** Read-only packages. For "what would this cost", not "let me buy it". */
export const PRICING_PATH = '/pricing';
